import type { StateCreator } from 'zustand/vanilla';
import {
  AUDIO_DURATION,
  DEFAULT_CHARACTER2VIDEO_FORM_PARAMS,
  DEFAULT_FORM_PARAMS,
  DEFAULT_IMAGE_FORM_PARAMS,
  DEFAULT_IMG2VIDEO_FORM_PARAMS,
  DEFAULT_SCENE_MAX_IMAGE_COUNT,
  MODEL_VERSION,
  MODEL_VERSION_SETTINGS_MAP,
  SEGMENTS_COLOR_LADDER,
} from '@/app/constants';
import type {
  FormParams,
  FormTabType,
  Img2VideoFormParams,
  ModelVersion,
  ReferencePrompt,
  TaskSettings,
  TemplateFormParams,
  Text2AudioFormParams,
  ToolFormTabType,
  Trials,
  VideoToolValues,
} from '@/app/types';
import {
  getAudioImageIndex,
  getPromptMaxLength,
  getTextPromptLength,
  isMembershipRevisionActive,
  truncateContentToMaxLength,
  validateUserPermissionSettings,
} from '@/app/utils';
import type { UserInfoStore } from './user-info';

export type { FormParams, TemplateFormParams, Text2AudioFormParams, Trials } from '@/app/types';

type TaskFormState = Trials & {
  homeGenerationMode: Exclude<FormTabType, 'scene'>;
  img2videoFormParams: Img2VideoFormParams; // 图生
  text2videoFormParams: FormParams; // 文生
  character2videoFormParams: FormParams; // 参考生
  sceneFormParams: TemplateFormParams; // 模板
  text2audioFormParams: Text2AudioFormParams; // 音频
  reference2imageFormParams: FormParams; // 参考生图
  text2imageFormParams: FormParams; // 文生图
  storyGridFormParams: FormParams; // 分镜
  lastActiveVideoTool?: VideoToolValues; // 最近激活的视频工具
  hiddenReferencePrompts?: Partial<Record<string, ReferencePrompt[]>>;
};

export interface TaskFormStore extends TaskFormState {
  /**
   * 一次性更新全部 trials
   */
  updateTrialsAll: (trials: Trials) => void;

  // 更新模型版本
  updateModelVersion: (tabType: Exclude<FormTabType, 'scene'>, modelVersion: ModelVersion) => boolean;
  updateHomeGenerationMode: (mode: Exclude<FormTabType, 'scene'>) => void;
  // 更新表单参数
  updateFormParams: (
    tabType: Exclude<FormTabType | ToolFormTabType, 'scene'>,
    key: keyof FormParams,
    params: FormParams[typeof key],
  ) => void;
  updateTemplateFormParams: (key: keyof TemplateFormParams, params: TemplateFormParams[typeof key]) => void;

  // 设置表单参数
  setFormParams: (
    tabType: Exclude<FormTabType | ToolFormTabType, 'video2extail'>,
    formParams: Partial<FormParams | TemplateFormParams>,
  ) => void;

  // 重置任务表单整个 slice 状态
  resetTaskFormStates: () => void;

  // 检查设置是否对当前用户可用，否则应用默认值
  getValidSettings: (
    tabType: Exclude<FormTabType, 'scene'>,
    settings: TaskSettings,
  ) => {
    settings: TaskSettings;
    hasInvalidSetting: boolean;
  };
  // 更新 text2audio 表单参数
  updateText2AudioFormParams: (params: Text2AudioFormParams) => void;

  setChar2VideoWarningTipVisible: (flag: boolean) => void;
  /**
   * 更新最近激活的视频工具
   * @param tool 视频工具
   */
  updateLastActiveVideoTool: (tool: VideoToolValues) => void;
  /**
   * 存储隐藏的 referencePrompts（用于模型版本切换）
   * @param capabilityKey 模型能力 key
   * @param items 隐藏的项
   */
  setHiddenReferencePrompts: (capabilityKey: string, items: ReferencePrompt[]) => void;
}

const defaultTrials: Trials = {
  trials720p: {
    total: 0,
    remaining: 0,
  },
  trialsQ1: {
    total: 0,
    remaining: 0,
  },
  trialsQ2T2V: {
    total: 0,
    remaining: 0,
  },
  trialsQ2C2V: {
    total: 0,
    remaining: 0,
  },
  trialsQ2T2I: {
    total: 0,
    remaining: 0,
  },
  trialsQ2R2I: {
    total: 0,
    remaining: 0,
  },
  trialsImage25T2IFast: {
    total: 0,
    remaining: 0,
  },
  trialsImage25T2IPro: {
    total: 0,
    remaining: 0,
  },
  trialsImage25R2IFast: {
    total: 0,
    remaining: 0,
  },
  trialsImage25R2IPro: {
    total: 0,
    remaining: 0,
  },
  trialsQ2TransitionSpeed: {
    total: 0,
    remaining: 0,
  },
  trialsQ2TransitionPro: {
    total: 0,
    remaining: 0,
  },
  trialsText2Audio: {
    total: 0,
    remaining: 0,
  },
  trialsVoice: {
    total: 0,
    remaining: 0,
  },
  trialsProMode: {
    total: 0,
    remaining: 0,
  },
  trialsQ2Extend: {
    total: 0,
    remaining: 0,
  },
  trialsQ2ExtendPro: {
    total: 0,
    remaining: 0,
  },
  trialsQ2Pro: {
    total: 0,
    remaining: 0,
  },
  trialsQ3T2VI2V: {
    total: 0,
    remaining: 0,
  },
  trialsQ3C2V: {
    total: 0,
    remaining: 0,
  },
  trialsQ4_540p: {
    total: 0,
    remaining: 0,
  },
  trialsQ4_720p: {
    total: 0,
    remaining: 0,
  },
  trialsQ4_1080p: {
    total: 0,
    remaining: 0,
  },
  trialsStoryGrid: {
    total: 0,
    remaining: 0,
  },
  trialsOfflineAvatar: {
    total: 0,
    remaining: 0,
  },
};

const defaultSceneFormParams: TemplateFormParams = {
  imagePrompts: [],
  textPrompt: '',
  creationResolution: '512',
  isMergeImage: false,
  maxImageCount: DEFAULT_SCENE_MAX_IMAGE_COUNT,
};

const defaultText2AudioFormParams: Text2AudioFormParams = {
  segments: [],
  settings: {
    sample_count: 1,
    schedule_mode: 'normal',
    duration: AUDIO_DURATION,
    model_version: MODEL_VERSION.Q1,
  },
};

// 静态初始状态
const initialState: TaskFormState = {
  ...defaultTrials,
  homeGenerationMode: 'character2video',
  img2videoFormParams: { ...DEFAULT_IMG2VIDEO_FORM_PARAMS },
  text2videoFormParams: {
    ...DEFAULT_FORM_PARAMS,
  },
  character2videoFormParams: {
    ...DEFAULT_CHARACTER2VIDEO_FORM_PARAMS,
  },
  reference2imageFormParams: {
    ...DEFAULT_IMAGE_FORM_PARAMS,
  },
  text2imageFormParams: {
    ...DEFAULT_IMAGE_FORM_PARAMS,
  },
  storyGridFormParams: {
    ...DEFAULT_IMAGE_FORM_PARAMS,
  },
  sceneFormParams: {
    ...defaultSceneFormParams,
  },
  text2audioFormParams: {
    ...defaultText2AudioFormParams,
  },
  hiddenReferencePrompts: {},
};

const SETTINGS_VERSIONS = [
  'settingsV2_0',
  'settingsQ1',
  'settingsQ2',
  'settingsQ2_pro',
  'settingsQ3',
  'settingsQ3_fast',
  'settingsQ3_lite',
  'settingsQ3_pro',
  'settingsGPTImage2',
  'settingsGPTImage25Fast',
  'settingsGPTImage25Pro',
  'settingsQ3_a',
  'settingsQ3_hh',
  'settingsQ4',
];
export const createTaskFormSlice: StateCreator<TaskFormStore & UserInfoStore, [], [], TaskFormStore> = (set, get) => ({
  ...initialState,

  updateTrialsAll: (trials: Trials) => {
    set({
      ...trials,
    });
  },
  updateModelVersion: (tabType: Exclude<FormTabType, 'scene'>, modelVersion: ModelVersion) => {
    let didTruncate = false;
    set((state) => {
      const formParams = state[`${tabType}FormParams`];
      const maxPromptLength = getPromptMaxLength(modelVersion);
      didTruncate = formParams.textPrompt.length > maxPromptLength;
      const textPromptWithReference = formParams.textPromptWithReference
        ? truncateContentToMaxLength(formParams.textPromptWithReference, maxPromptLength)
        : formParams.textPromptWithReference;
      didTruncate ||=
        !!formParams.textPromptWithReference &&
        getTextPromptLength(formParams.textPromptWithReference) > maxPromptLength;

      return {
        [`${tabType}FormParams`]: {
          ...formParams,
          modelVersion,
          textPrompt: formParams.textPrompt.slice(0, maxPromptLength),
          textPromptWithReference,
        },
      };
    });
    return didTruncate;
  },
  updateHomeGenerationMode: (mode: Exclude<FormTabType, 'scene'>) => {
    set({
      homeGenerationMode: mode,
    });
  },
  updateFormParams: (
    tabType: Exclude<FormTabType | ToolFormTabType, 'scene'>,
    key: keyof FormParams,
    params: FormParams[typeof key],
  ) => {
    /**
     * 局部更新，需要保留部分原始值，例如
     * tabType:img2video, key:settingsQ1, params:{ duration: 10 }，只更新 duration，其他值保留
     * tabType:character2video, key:settingsQ1, params:{ resolution: "512" }，只更新 resolution，其他值保留
     */
    if (typeof params === 'object' && SETTINGS_VERSIONS.includes(key)) {
      set((state) => ({
        [`${tabType}FormParams`]: {
          ...state[`${tabType}FormParams`],
          [key]: {
            ...(state[`${tabType}FormParams`][key] as TaskSettings),
            ...params,
          },
        },
      }));
      return;
    }

    /**
     * 直接更新，整体替换，例如
     * tabType:character2video, key:textPrompt, params:"天气不错"
     * tabType:character2video, key:imagePrompts, params:[ null, {...} ]
     */
    set((state) => ({
      [`${tabType}FormParams`]: {
        ...state[`${tabType}FormParams`],
        [key]: params,
      },
    }));
  },
  updateTemplateFormParams: (key: keyof TemplateFormParams, params: TemplateFormParams[typeof key]) => {
    set((state) => ({
      sceneFormParams: {
        ...state.sceneFormParams,
        [key]: params,
      },
    }));
  },
  updateText2AudioFormParams: (params: Text2AudioFormParams) => {
    set((state) => ({
      text2audioFormParams: {
        ...state.text2audioFormParams,
        segments: params.segments
          ? params.segments?.map((segment) => ({
              ...segment,
              // 统一收口色值设置
              color: SEGMENTS_COLOR_LADDER[getAudioImageIndex(segment.id, SEGMENTS_COLOR_LADDER.length)],
            }))
          : state.text2audioFormParams.segments,
        settings: {
          ...state.text2audioFormParams.settings,
          ...(params.settings || {}),
        },
      },
    }));
  },
  updateLastActiveVideoTool: (tool: VideoToolValues) => {
    set({ lastActiveVideoTool: tool });
  },
  setFormParams: (
    tabType: Exclude<FormTabType | ToolFormTabType, 'video2extail'>,
    formParams: Partial<FormParams | TemplateFormParams>,
  ) => {
    set((state) => ({
      [`${tabType}FormParams`]: {
        ...state[`${tabType}FormParams`],
        ...formParams,
      },
    }));
  },

  resetTaskFormStates: () => {
    set({
      ...initialState,
    });
  },

  /**
   * 校验并修正任务设置，确保其符合当前用户的订阅计划和权限。
   * 如果某项设置不符合用户权限（例如选择了付费计划才有的分辨率），则将其重置为当前模型版本对应的默认值。
   *
   * @param tabType 表单类型（不包括 scene）
   * @param settings 用户输入的原始任务设置对象，可能是不同模型版本对应的设置类型。
   * @returns 返回一个对象，包含:
   *   - settings: 经过校验和可能修正后的设置对象。
   *   - hasInvalidSetting: 布尔值，表示是否至少有一项设置因权限问题被重置为默认值。
   */
  getValidSettings: (tabType: Exclude<FormTabType, 'scene'>, settings: TaskSettings) => {
    const state = get();
    const { trials720p, userInfo, trialsQ2TransitionPro } = state;
    const modelVersion = (state[`${tabType}FormParams`] as FormParams).modelVersion;
    const versionKey = MODEL_VERSION_SETTINGS_MAP[modelVersion];
    const defaultFormParams =
      tabType === 'character2video'
        ? DEFAULT_CHARACTER2VIDEO_FORM_PARAMS
        : tabType === 'img2video'
          ? DEFAULT_IMG2VIDEO_FORM_PARAMS
          : DEFAULT_FORM_PARAMS;

    return validateUserPermissionSettings({
      tabType,
      settings,
      modelVersion,
      userSubsPlan: userInfo?.subs_plan,
      isNewMembership: isMembershipRevisionActive(userInfo),
      trials720p,
      trialsQ2TransitionPro,
      defaultSettings: defaultFormParams[versionKey],
    });
  },

  setChar2VideoWarningTipVisible: (flag: boolean) => {
    const { character2videoFormParams } = get();
    // 只有在专业模式时才显示警告
    if (character2videoFormParams.isProMode) {
      set((state) => ({
        character2videoFormParams: {
          ...state.character2videoFormParams,
          isProModeWarningTipVisible: flag,
        },
      }));
    }
  },

  setHiddenReferencePrompts: (capabilityKey: string, items: ReferencePrompt[]) => {
    set((state) => ({
      hiddenReferencePrompts: {
        ...(state.hiddenReferencePrompts || {}),
        [capabilityKey]: items,
      },
    }));
  },
});
